import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { useServicesList } from '@/hooks/useServices';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Banner } from '@/components/ui/Banner';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Radio } from '@/components/ui/Radio';
import { Select } from '@/components/ui/Select';
import { Toggle } from '@/components/ui/Toggle';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  SaveBar,
  SectionCard,
  SettingsChoiceRow,
  SettingsEmpty,
  SettingsFormGrid,
  SettingsHint,
  SettingsLoading,
  SettingsStack,
} from './settingsChrome';
import type { Json } from '@/types/database.generated';
import {
  DEFAULT_ALLOW_PATIENT_RESCHEDULING,
  DEFAULT_CANCELLATION_DEADLINE_HOURS,
  DEFAULT_PAYMENT_TIMING,
  parseBookingPaymentMethods,
  parsePaymentTiming,
  type BookingPaymentMethods,
  type PatientCancelPermission,
  type PaymentTiming,
} from '@/lib/bookingPolicies';

export function ClinicOnlineBooking() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading: clinicLoading, error: clinicError } = useClinic(activeClinicId);
  const { data: services = [] } = useServicesList(activeClinicId, { includeInactive: false });
  const { data: members = [] } = useClinicMembers(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [enabled, setEnabled] = useState(false);
  const [advanceDays, setAdvanceDays] = useState(60);
  const [minHoursBefore, setMinHoursBefore] = useState(2);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedClinicianIds, setSelectedClinicianIds] = useState<string[]>([]);
  const [paymentTiming, setPaymentTiming] = useState<PaymentTiming>(DEFAULT_PAYMENT_TIMING);
  const [paymentMethods, setPaymentMethods] = useState<BookingPaymentMethods>({
    card: true,
    mobilepay: true,
    invoice: true,
    punch_card: true,
  });
  const [cancellationDeadlineHours, setCancellationDeadlineHours] = useState(
    DEFAULT_CANCELLATION_DEADLINE_HOURS,
  );
  const [allowPatientRescheduling, setAllowPatientRescheduling] = useState(
    DEFAULT_ALLOW_PATIENT_RESCHEDULING,
  );
  const [patientCancelBefore, setPatientCancelBefore] = useState<PatientCancelPermission>('allow');
  const [patientCancelAfter, setPatientCancelAfter] = useState<PatientCancelPermission>('deny');
  const [showPractitioners, setShowPractitioners] = useState(true);

  useEffect(() => {
    if (!clinic) return;
    const c = clinic as typeof clinic & {
      online_booking_enabled?: boolean;
      online_booking_advance_days?: number;
      online_booking_min_hours_before?: number;
      online_booking_services?: string[];
      online_booking_clinicians?: string[];
      payment_timing?: string;
      booking_payment_methods?: Json;
      cancellation_deadline_hours?: number;
      allow_patient_rescheduling?: boolean;
      patient_cancel_before_deadline?: string;
      patient_cancel_after_deadline?: string;
      show_practitioners_in_online_booking?: boolean;
    };
    setEnabled(c.online_booking_enabled ?? false);
    setAdvanceDays(c.online_booking_advance_days ?? 60);
    setMinHoursBefore(c.online_booking_min_hours_before ?? 2);
    setSelectedServiceIds(c.online_booking_services ?? []);
    setSelectedClinicianIds(c.online_booking_clinicians ?? []);
    setPaymentTiming(parsePaymentTiming(c.payment_timing));
    setPaymentMethods(parseBookingPaymentMethods(c.booking_payment_methods));
    setCancellationDeadlineHours(c.cancellation_deadline_hours ?? DEFAULT_CANCELLATION_DEADLINE_HOURS);
    setAllowPatientRescheduling(Boolean(c.allow_patient_rescheduling));
    setPatientCancelBefore(c.patient_cancel_before_deadline === 'deny' ? 'deny' : 'allow');
    setPatientCancelAfter(c.patient_cancel_after_deadline === 'allow' ? 'allow' : 'deny');
    setShowPractitioners(c.show_practitioners_in_online_booking !== false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic?.id]);

  if (clinicLoading) return <SettingsLoading />;
  if (clinicError) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        online_booking_enabled: enabled,
        online_booking_advance_days: advanceDays,
        online_booking_min_hours_before: minHoursBefore,
        online_booking_services: selectedServiceIds as unknown as Json,
        online_booking_clinicians: selectedClinicianIds as unknown as Json,
        payment_timing: paymentTiming,
        booking_payment_methods: paymentMethods as unknown as Json,
        cancellation_deadline_hours: cancellationDeadlineHours,
        allow_patient_rescheduling: allowPatientRescheduling,
        patient_cancel_before_deadline: patientCancelBefore,
        patient_cancel_after_deadline: patientCancelAfter,
        show_practitioners_in_online_booking: showPractitioners,
      };
      await update.mutateAsync(payload);
      toast.success(t('settings.saved_toast'));
    } catch (err) {
      console.error('update clinic online booking settings failed', err);
      const errorMessage =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : '';
      if (errorMessage.includes('forbidden') || errorMessage.includes('admin required')) {
        toast.error(t('ob_settings.admin_required_error'));
      } else {
        toast.error(t('settings.save_error_toast'));
      }
    }
  };

  const toggleServiceId = (id: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleClinicianId = (id: string) => {
    setSelectedClinicianIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const bookingUrl = `${window.location.origin}/book/${(clinic as { slug?: string }).slug ?? ''}`;

  return (
    <form onSubmit={onSubmit} className="flex min-w-0 flex-col gap-s4">
      <SectionCard
        title={t('ob_settings.enable_title')}
        subtitle={t('ob_settings.enable_subtitle')}
        readOnly={!canEdit}
      >
        <Toggle
          checked={enabled}
          disabled={!canEdit}
          onCheckedChange={setEnabled}
          label={t('ob_settings.enable_label')}
          hint={t('ob_settings.enable_desc')}
        />

        {enabled ? (
          <Banner tone="info" className="mt-s4">
            <span className={cn(TYPE.small, 'block')}>{t('ob_settings.booking_url_label')}</span>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-s1 inline-flex min-w-0 items-center gap-s2 font-mono text-ds-teal hover:underline"
            >
              <Globe className="h-4 w-4 shrink-0" aria-hidden />
              <span className="min-w-0 break-all">{bookingUrl}</span>
            </a>
          </Banner>
        ) : null}
      </SectionCard>

      <SectionCard
        title={t('ob_settings.show_practitioners_title')}
        subtitle={t('ob_settings.show_practitioners_subtitle')}
        readOnly={!canEdit}
      >
        <Toggle
          checked={showPractitioners}
          disabled={!canEdit}
          onCheckedChange={setShowPractitioners}
          label={t('ob_settings.show_practitioners_label')}
          hint={t('ob_settings.show_practitioners_desc')}
        />
      </SectionCard>

      <SectionCard
        title={t('booking_policies.payment_timing_title')}
        subtitle={t('booking_policies.payment_timing_subtitle')}
        readOnly={!canEdit}
      >
        <fieldset>
          <legend className={cn(TYPE.label, 'text-ds-text')}>{t('booking_policies.when_pay')}</legend>
          <SettingsStack className="mt-s3">
            {(
              [
                ['upon_booking', 'booking_policies.upon_booking', 'booking_policies.upon_booking_help'],
                ['at_appointment', 'booking_policies.at_appointment', 'booking_policies.at_appointment_help'],
              ] as const
            ).map(([value, labelKey, helpKey]) => (
              <SettingsChoiceRow key={value}>
                <Radio
                  name="payment_timing"
                  value={value}
                  checked={paymentTiming === value}
                  disabled={!canEdit}
                  onChange={() => setPaymentTiming(value)}
                  label={t(labelKey)}
                  hint={t(helpKey)}
                />
              </SettingsChoiceRow>
            ))}
          </SettingsStack>
        </fieldset>

        <div className="mt-s6">
          <p className={cn(TYPE.label, 'text-ds-text')}>{t('booking_policies.methods_title')}</p>
          <SettingsHint className="mt-s1">{t('booking_policies.methods_subtitle')}</SettingsHint>
          <SettingsStack className="mt-s3">
            {(
              [
                ['card', 'booking_policies.method_card'],
                ['mobilepay', 'booking_policies.method_mobilepay'],
                ['punch_card', 'booking_policies.method_punch_card'],
                ['invoice', 'booking_policies.method_invoice'],
              ] as const
            ).map(([key, labelKey]) => (
              <Checkbox
                key={key}
                checked={paymentMethods[key]}
                disabled={!canEdit}
                onChange={() => setPaymentMethods((prev) => ({ ...prev, [key]: !prev[key] }))}
                label={t(labelKey)}
              />
            ))}
          </SettingsStack>
        </div>
      </SectionCard>

      <SectionCard
        title={t('booking_policies.cancel_reschedule_title')}
        subtitle={t('booking_policies.cancel_reschedule_subtitle')}
        readOnly={!canEdit}
      >
        <SettingsStack>
          <Input
            id="cancellation-deadline-hours"
            type="number"
            min={0}
            max={8760}
            label={t('booking_policies.deadline_hours')}
            value={cancellationDeadlineHours}
            disabled={!canEdit}
            onChange={(e) => setCancellationDeadlineHours(Number(e.target.value))}
            hint={t('booking_policies.deadline_hours_help')}
          />
          <details className="rounded-lg border border-ds-line px-3 py-2">
            <summary className="cursor-pointer text-sm text-ds-text">
              {t('booking_policies.fee_more', 'Flere muligheder')}
            </summary>
            <p className="mt-2 text-sm text-ds-muted">
              {t(
                'booking_policies.fee_help',
                'Gebyr for udeblivelse og sen aflysning er slået fra. Det er ikke et krav. Beløbet sættes på den enkelte ydelse.',
              )}
            </p>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Boolean(
                  (clinic as { cancellation_fee_enabled?: boolean }).cancellation_fee_enabled,
                )}
                disabled={!canEdit}
                onChange={(event) => {
                  void supabase.rpc('set_cancellation_fee_enabled', {
                    p_clinic_id: clinic.id,
                    p_enabled: event.target.checked,
                  });
                }}
              />
              {t('booking_policies.fee_toggle', 'Brug gebyr')}
            </label>
          </details>

          <SettingsFormGrid className="mt-0">
            <Select
              id="patient-cancel-before"
              label={t('booking_policies.patient_cancel_before')}
              value={patientCancelBefore}
              disabled={!canEdit}
              onChange={(e) => setPatientCancelBefore(e.target.value as PatientCancelPermission)}
            >
              <option value="allow">{t('booking_policies.permission_allow')}</option>
              <option value="deny">{t('booking_policies.permission_deny')}</option>
            </Select>
            <Select
              id="patient-cancel-after"
              label={t('booking_policies.patient_cancel_after')}
              value={patientCancelAfter}
              disabled={!canEdit}
              onChange={(e) => setPatientCancelAfter(e.target.value as PatientCancelPermission)}
            >
              <option value="allow">{t('booking_policies.permission_allow')}</option>
              <option value="deny">{t('booking_policies.permission_deny')}</option>
            </Select>
          </SettingsFormGrid>

          <fieldset>
            <legend className={cn(TYPE.label, 'text-ds-text')}>
              {t('booking_policies.patient_reschedule')}
            </legend>
            <SettingsStack className="mt-s2">
              {(
                [
                  [true, 'booking_policies.patient_reschedule_yes'],
                  [false, 'booking_policies.patient_reschedule_no'],
                ] as const
              ).map(([value, labelKey]) => (
                <Radio
                  key={String(value)}
                  name="allow_patient_rescheduling"
                  checked={allowPatientRescheduling === value}
                  disabled={!canEdit}
                  onChange={() => setAllowPatientRescheduling(value)}
                  label={t(labelKey)}
                />
              ))}
            </SettingsStack>
          </fieldset>
          <SettingsHint>{t('booking_policies.practitioner_reschedule_note')}</SettingsHint>
          <SettingsHint>{t('booking_policies.financial_policies_note')}</SettingsHint>
        </SettingsStack>
      </SectionCard>

      <SectionCard
        title={t('ob_settings.limits_title')}
        subtitle={t('ob_settings.limits_subtitle')}
        readOnly={!canEdit}
      >
        <SettingsFormGrid className="mt-0">
          <Input
            id="advance-days"
            type="number"
            min={1}
            max={365}
            label={t('ob_settings.advance_days_label')}
            value={advanceDays}
            disabled={!canEdit}
            onChange={(e) => setAdvanceDays(Number(e.target.value))}
            hint={t('ob_settings.advance_days_hint')}
          />
          <Input
            id="min-hours-before"
            type="number"
            min={0}
            max={72}
            label={t('ob_settings.min_hours_label')}
            value={minHoursBefore}
            disabled={!canEdit}
            onChange={(e) => setMinHoursBefore(Number(e.target.value))}
            hint={t('ob_settings.min_hours_hint')}
          />
        </SettingsFormGrid>
      </SectionCard>

      <SectionCard
        title={t('ob_settings.hours_title')}
        subtitle={t('ob_settings.hours_canonical_subtitle')}
      >
        <p className={cn(TYPE.body, 'text-ds-text')}>{t('ob_settings.hours_canonical_body')}</p>
      </SectionCard>

      <SectionCard
        title={t('ob_settings.services_title')}
        subtitle={t('ob_settings.services_subtitle')}
        readOnly={!canEdit}
      >
        {services.length === 0 ? (
          <SettingsEmpty title={t('ob_settings.services_empty')} icon={null} />
        ) : (
          <SettingsStack>
            {services.map((svc) => (
              <SettingsChoiceRow key={svc.id} className="items-center">
                <Checkbox
                  checked={selectedServiceIds.includes(svc.id)}
                  disabled={!canEdit}
                  onChange={() => toggleServiceId(svc.id)}
                  label={
                    <span className="flex min-w-0 items-center gap-s2">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: svc.color }}
                      />
                      <span>
                        <span className={cn(TYPE.body, 'block')}>{svc.name}</span>
                        <span className={cn(TYPE.small, 'text-ds-muted')}>
                          {svc.duration_minutes} min
                        </span>
                      </span>
                    </span>
                  }
                />
              </SettingsChoiceRow>
            ))}
          </SettingsStack>
        )}
      </SectionCard>

      <SectionCard
        title={t('ob_settings.clinicians_title')}
        subtitle={t('ob_settings.clinicians_subtitle')}
        readOnly={!canEdit}
      >
        {members.length === 0 ? (
          <SettingsEmpty title={t('ob_settings.clinicians_empty')} icon={null} />
        ) : (
          <SettingsStack>
            {members.map((m) => (
              <SettingsChoiceRow key={m.user_id} className="items-center">
                <Checkbox
                  checked={selectedClinicianIds.includes(m.user_id)}
                  disabled={!canEdit}
                  onChange={() => toggleClinicianId(m.user_id)}
                  label={
                    <span>
                      <span className={cn(TYPE.body, 'block')}>{m.name}</span>
                      <span className={cn(TYPE.small, 'text-ds-muted')}>{m.email}</span>
                    </span>
                  }
                />
              </SettingsChoiceRow>
            ))}
          </SettingsStack>
        )}
      </SectionCard>

      <SaveBar canEdit={canEdit} pending={update.isPending} />
    </form>
  );
}
